/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HmfSelectorComponent, HmfTextLinkComponent } from '@dsg-tech/homefield-angular';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { HomefieldTextLinkModule } from '@ecomm-patterns-components';
import { IsBotService } from '@shared/data-access/services';
import { ProductAnalyticsService } from '@shared/data-access/analytics';

@Component({
  selector: 'shared-totf-components-product-card-color-swatches',
  imports: [CommonModule, HomefieldTextLinkModule, HmfSelectorComponent, HmfTextLinkComponent],
  templateUrl: './product-card-color-swatches.component.html',
  styleUrl: './product-card-color-swatches.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCardColorSwatchesComponent implements OnInit, OnDestroy {
  private readonly isBotService = inject(IsBotService);
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly assetSeoUrl = input('');
  readonly colors = input<string[]>([]);
  readonly ecode = input('');
  readonly isLazy = input(true);

  readonly extraColorsClick = output<Event>();
  readonly selectedSwatchColorChange = output<string>();

  readonly IMAGE_HOST = 'https://dks.scene7.com/is/image/dkscdn';

  readonly maxColorSwatchesToShow = signal(3);
  readonly userSelectedSwatchColor = signal('');
  readonly failedSwatchColors = signal(new Set<string>());
  readonly selectedSwatchColor = computed(() => {
    if (this.userSelectedSwatchColor()) {
      return this.userSelectedSwatchColor();
    }
    if (this.validColors().length) {
      return this.validColors()[0];
    }
    return '';
  });

  readonly remainingColors = computed(
    () => this.validColors().length - this.maxColorSwatchesToShow(),
  );

  readonly moreColorsButtonAriaLabel = computed(
    () =>
      `Show ${this.remainingColors()} more color option${this.remainingColors() > 1 ? 's' : ''}.`,
  );

  readonly validColors: Signal<string[]> = computed(
    () =>
      this.colors()?.filter(
        (color) => color !== null && color !== undefined && color.trim() !== '',
      ) || [],
  );

  readonly imageSourceMap = computed(
    () =>
      new Map(
        this.validColors().map((color) => [
          color,
          computed(() =>
            this.failedSwatchColors().has(color)
              ? DCSG_CONSTANTS.imageUnavailable
              : this.getImageSource(color),
          ),
        ]),
      ),
  );

  private widthQuery?: MediaQueryList;

  ngOnInit(): void {
    this.setNumberOfSwatchesToShow();
  }

  trackBySwatch(index: number, item: string): string {
    return `${index}-${item}`;
  }

  setImageColor(color: string): void {
    if (color === this.userSelectedSwatchColor()) {
      return;
    }

    this.userSelectedSwatchColor.set(color);
    this.selectedSwatchColorChange.emit(color);
  }

  getImageSource(color: string): string {
    return `${this.IMAGE_HOST}/${this.ecode()}_${color.replace(
      /[^\w]/gi,
      '_',
    )}_swatch/?$DSG_ProductSwatch$&resMode=bilin&qlt=80`;
  }

  extraColorsClicked(event: Event): void {
    this.extraColorsClick.emit(event);
  }

  /**
   * @description Posts to rumrunner when a missing product image is detected
   */
  handleImageError(event: { color: string; requestedImg: string }): void {
    this.failedSwatchColors.update((set) => new Set(set).add(event.color));

    if (
      isPlatformBrowser(this.platformId) &&
      !this.isBotService.isBot(window.navigator.userAgent)
    ) {
      this.analyticsService.emitMissingImageEvent({
        pageUrl: window.location.href,
        requestedImg: event.requestedImg,
        ecode: this.ecode(),
        color: event.color,
      });
    }
  }

  private _widthQueryChangeHandler(event: MediaQueryListEvent) {
    this.maxColorSwatchesToShow.set(event.matches ? 3 : 6);
  }

  private readonly widthQueryChangeHandler = this._widthQueryChangeHandler.bind(this);

  private setNumberOfSwatchesToShow(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.widthQuery = matchMedia('(max-width: 1531px)');

    // Initial check of the media query
    this.maxColorSwatchesToShow.set(this.widthQuery?.matches ? 3 : 6);

    // Check when window width passes the media query
    this.widthQuery?.addEventListener('change', this.widthQueryChangeHandler);
  }

  ngOnDestroy() {
    this.widthQuery?.removeEventListener('change', this.widthQueryChangeHandler);
  }
}
